import {
  addDisplayName,
  addHandlers,
  addProps,
  addState,
  addWrapper,
  addEffect,
  flowMax,
} from 'ad-hok'
import { addEffectOnMount } from 'ad-hok-utils'
import { compact } from 'lodash/fp'
import React, { FC } from 'react'
import { isMobile } from 'react-device-detect'
import { Helmet } from 'react-helmet-async'
import { createUseStyles } from 'react-jss'
import { Product as SchemaProduct, WithContext } from 'schema-dts'
import { addApi } from 'shared/src/api'
import {
  ProductDiscoverySource,
  ProductWithImage,
  User,
  isProductDiscoverySource,
} from 'shared/src/api/types'
import {
  addIntPathParam,
  addNavigate,
  addOptionalIntQueryParam,
  addOptionalQueryParam,
} from 'shared/src/utils/addNavigate'
import { addNavigationState } from 'shared/src/utils/addNavigationState'
import {
  trackProductOutgoingClick,
  trackProductView,
} from 'shared/src/utils/analytics'
import { addDataLoadingFromPromise } from 'shared/src/utils/dataLoading'
import { formattedUsd } from 'shared/src/utils/formatting'
import { typedAs } from 'shared/src/utils/typedAs'
import { navBackOrHomepage } from 'shared/src/utils/navBack'
import { Link } from 'react-router-dom'

import { ReactComponent as BackIcon } from '../../../../assets/chevron-left-slim.svg'
import productExplore from 'assets/product-explore.svg'
import { Button } from 'components/Button'
import { addLaunchContext } from 'utils/launchContext'
import { setPageTitle } from 'utils/title'
import { addLoadingAndErrorIndicators } from '../../../../utils/addLoadingAndErrorIndicators'
import { addTranslationHelpers } from '../../../../utils/addTranslationHelpers'
import { constrainWidth } from '../../../../utils/layout'
import { makeMediaUrl } from '../../../../utils/routes'
import { sansSerifFontFamily, serifFontFamily } from '../../../../utils/styles'
import {
  PRODUCT_DETAIL_ABOVE_IMAGE_Z_INDEX,
  PRODUCT_DETAIL_IMAGE_Z_INDEX,
} from '../../../../utils/zIndex'
import { BookmarkButton } from '../../../BookmarkButton'
import { WhiteButton } from '../../../WhiteButton'
import { addMenuPage } from '../../../menu'
import { addRegisterPage } from '../../../register'
import downloadAppBanner from 'assets/download_app_banner.png'
import cornerLogo from '../../../../assets/top_corner_r.png'
import { APP_WAITLIST_URL } from '../../../../utils/urls'

const makeSchemaProduct = (
  product: ProductWithImage
): WithContext<SchemaProduct> =>
  ({
    '@context': 'https://schema.org',
    '@type': 'Product',
    brand: {
      '@type': 'Brand',
      name: product.brandName,
    },
    name: product.name,
    image: product.image.full,
    offers: compact([
      product.price !== null
        ? {
            '@type': 'Offer',
            price: product.price / 100,
            priceCurrency: 'USD',
          }
        : null,
    ]),
  } as const)

const makeProductUrl = (
  urlString: string,
  {
    productId,
    mediaId,
    userListId,
  }: {
    productId: number
    mediaId?: number
    userListId?: number
    user?: User
  }
): string => {
  const url = new URL(urlString)

  url.searchParams.append('_rodeo_product_id', productId.toString())

  if (mediaId) {
    url.searchParams.append('_rodeo_media_id', mediaId.toString())
  }
  if (userListId) {
    url.searchParams.append('_rodeo_user_list_id', userListId.toString())
  }

  return url.toString()
}

const hasHistory = () =>
  window.history.length > 2 && document.referrer !== window.location.origin

const BLUR_RADIUS = 10

const useStyles = createUseStyles({
  container: {
    backgroundColor: '#101010',
    minHeight: '100%',
    position: 'relative',
    paddingBottom: 5,
  },
  closeButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    zIndex: PRODUCT_DETAIL_ABOVE_IMAGE_Z_INDEX,
  },
  infoContainer: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: 24,
    paddingRight: 24,
    paddingTop: 16,
    paddingBottom: 16,
  },
  imageContainer: {
    width: '100%',
    background: 'none',
    border: 'none',
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  blurredImage: {
    position: 'absolute',
    top: -2 * BLUR_RADIUS,
    left: -2 * BLUR_RADIUS,
    width: `calc(100% + 4 * ${BLUR_RADIUS}px)`,
    height: `calc(100% + 4 * ${BLUR_RADIUS}px)`,
    filter: `blur(${BLUR_RADIUS}px)`,
    objectFit: 'cover',
    backgroundColor: '#ffffff',
  },
  image: {
    height: '274px',
    maxWidth: '100%',
    objectFit: 'contain',
    zIndex: PRODUCT_DETAIL_IMAGE_Z_INDEX,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
  },
  shopButton: {
    flexGrow: 1,
    flex: 1,
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    fontFamily: sansSerifFontFamily,
    fontWeight: 500,
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.6)',
  },
  namePriceContainer: {
    marginBottom: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  name: {
    margin: 0,
    fontWeight: 600,
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.8)',
  },
  price: {
    margin: 0,
    fontFamily: sansSerifFontFamily,
    fontWeight: 700,
    fontSize: 16,
    color: 'white',
    minWidth: 60,
    overflow: 'clip',
    textAlign: 'right',
  },
  featuredProductsTitle: {
    fontFamily: serifFontFamily,
    fontWeight: 500,
    fontSize: 18,
    color: 'white',
    marginTop: 0,
  },
  featuredProductsLasso: {
    marginBottom: 50,
  },
  titleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginLeft: 24,
    marginRight: 24,
    marginBottom: 8,
  },
  viewAllText: {
    color: '#CBA67A',
    textDecoration: 'none',
    fontSize: 12,
    fontFamily: sansSerifFontFamily,
    fontWeight: 500,
    lineHeight: '16px',
    alignSelf: 'center',
    paddingTop: 3,
  },
  brandProductsContainer: {
    display: 'flex',
    gap: 11,
    overflowX: 'scroll',
    marginLeft: 24,
    marginBottom: 16,
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  mediasContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 14,
    overflowX: 'scroll',
    marginBottom: 24,
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  mediaImage: {
    cursor: 'pointer',
    position: 'relative',
    height: 204,
    width: 163,
    objectFit: 'cover',
  },
  mediasTitle: {
    fontFamily: serifFontFamily,
    fontWeight: 500,
    fontSize: 16,
    color: 'white',
    marginTop: 0,
  },
  lassoTitle: {
    margin: 0,
    marginBottom: 100,
    marginLeft: 24,
  },
  rightBar: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    position: 'absolute',
    right: 24,
    top: 32,
    bottom: 36,
    pointerEvents: 'none',
  },
  rightBarTop: {
    paddingLeft: 10,
  },
  rightBarInner: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    pointerEvents: 'auto',
    zIndex: PRODUCT_DETAIL_ABOVE_IMAGE_Z_INDEX,
  },
  featuredCreatorsHeader: {
    fontFamily: serifFontFamily,
    fontWeight: 500,
    fontSize: 18,
    color: 'white',
    marginBottom: 32,
    marginLeft: 24,
  },
  affiliateDisclosure: {
    fontFamily: 'sans-serif',
    color: '#FFFFFF99',
    fontSize: 14,
    lineHeight: '20px',
    fontWeight: 400,
    marginTop: 41,
    marginBottom: 26,
    textAlign: 'center',
  },
  downloadAppBannerHolder: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  downloadAppBanner: {
    alignItems: 'center',
  },
  radarsContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  radarLink: {
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden',
    height: 404,
    width: 342,
    padding: 14,
    borderWidth: 0,
    borderRadius: 14,
    backgroundSize: 'cover',
  },
  radarTitle: {
    maxWidth: 284,
    fontFamily: serifFontFamily,
    fontWeight: 500,
    fontSize: 24,
    lineHeight: '120%',
    color: 'white',
    position: 'absolute',
    top: 267,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  radarIntro: {
    maxWidth: 284,
    fontFamily: 'sans-serif',
    fontWeight: 500,
    color: '#FFFFFF99',
    fontSize: 16,
    lineHeight: '140%',
    position: 'absolute',
    top: 304,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    lineClamp: 2,
    WebkitBoxOrient: 'vertical',
    textAlign: 'left',
  },
})

export const ProductDetail: FC = flowMax(
  addDisplayName('ProductDetail'),
  constrainWidth({ fillHeight: true }),
  addIntPathParam('productId'),
  addOptionalIntQueryParam('mediaId'),
  addOptionalIntQueryParam('userListId'),
  addOptionalQueryParam('creator'),
  addNavigationState(
    'discoverySource',
    'setDiscoverySource',
    typedAs<ProductDiscoverySource | undefined>(undefined)
  ),
  addWrapper((render, { productId }) => (
    <React.Fragment key={productId}>{render()}</React.Fragment>
  )),
  addApi,
  addLaunchContext,
  addState('imgSrc', 'setImgSrc', ''),
  addState(
    'hasSeenInitialRecommendations',
    'setSeenInitialRecommendations',
    false
  ),
  addEffectOnMount(({ setSeenInitialRecommendations }) => () => {
    setSeenInitialRecommendations(true)
  }),
  addDataLoadingFromPromise(
    'productDetails',
    ({ productId, mediaId, userListId, api }) =>
      api.products.details(productId, {
        mediaId,
        userListId,
      }),
    ['productId', 'mediaId'],
    ({ productId, mediaId }) => `productDetails-${productId}-${mediaId}`
  ),
  addDataLoadingFromPromise(
    'topMedias',
    ({ productId, mediaId, api }) => api.products.medias(productId, mediaId),
    ['productId'],
    ({ productId, mediaId }) =>
      `productDetails-topMedias-${productId}-${mediaId}`
  ),
  addDataLoadingFromPromise(
    'trendingMedias',
    ({ api }) => api.tags.paginatedFeed(1, 0, null),
    [],
    ({ productId, mediaId }) =>
      `productDetails-trending-${productId}-${mediaId}`
  ),
  addDataLoadingFromPromise(
    'user',
    ({ creator, api }) =>
      creator ? api.users.details(creator) : Promise.resolve(null),
    ['creator'],
    ({ creator }) => `productDetails-users-details-${creator}`
  ),
  addDataLoadingFromPromise(
    'radars',
    ({ api }) => api.radars.paginatedFeed(0, null),
    [],
    () => 'radars-feed'
  ),
  addLoadingAndErrorIndicators('productDetails', {
    redirectTo404IfError: true,
  }),
  addLoadingAndErrorIndicators('user', {
    redirectTo404IfError: true,
  }),
  addLoadingAndErrorIndicators('topMedias', {
    redirectTo404IfError: true,
  }),
  addLoadingAndErrorIndicators('trendingMedias', {
    redirectTo404IfError: true,
  }),
  addTranslationHelpers,
  addProps(() => ({
    classes: useStyles(),
  })),
  addProps(({ productDetails: { product } }) => ({
    product,
  })),
  addProps(({ product: { brandName, name }, t }) => ({
    title: t('productDetail.title', { brand: brandName, name }),
  })),
  setPageTitle(({ title }) => title),
  addProps(({ product }) => ({
    schemaOrg: JSON.stringify(makeSchemaProduct(product)),
  })),
  addEffectOnMount(
    ({
        productId,
        mediaId,
        userListId,
        user,
        discoverySource,
        api,
        product: { image },
        setImgSrc,
      }) =>
      () => {
        setImgSrc(image.lowres)
        trackProductView(
          {
            productId,
            mediaId,
            userListId,
            creatorId: user?.id,
            discoverySource: isProductDiscoverySource(discoverySource)
              ? discoverySource
              : undefined,
          },
          api
        )
      }
  ),
  addProps(({ creator, mediaId }) => ({
    origin:
      creator && mediaId
        ? {
            creatorUsername: creator,
            mediaId,
          }
        : null,
  })),
  addNavigate,
  addMenuPage,
  addRegisterPage,
  addState('isShuffling', 'setShuffling', false),
  addHandlers({
    onShop:
      ({
        mediaId,
        userListId,
        product: { id, url },
        user,
        discoverySource,
        api,
      }) =>
      async () => {
        const createOutgoingProductClickPromise = trackProductOutgoingClick(
          {
            mediaId,
            userListId,
            productId: id,
            analyticSource: undefined,
            discoverySource: isProductDiscoverySource(discoverySource)
              ? discoverySource
              : undefined,
          },
          user?.id,
          api
        )
        if (isMobile) {
          await createOutgoingProductClickPromise
          window.location.href = url
        } else {
          createOutgoingProductClickPromise.then(() => {
            // Handle successful click tracking
          })
          window.open(url, '_blank')
        }
      },
  }),
  addProps(
    ({
      product: { id, url: productUrl, originalUrl },
      mediaId,
      userListId,
      user,
    }) => ({
      productUrl: makeProductUrl(originalUrl ?? productUrl, {
        productId: id,
        mediaId,
        userListId,
        user: user ?? undefined,
      }),
    })
  ),
  addProps(({ topMedias }) => ({
    hasTopMedias: topMedias.length > 0,
  })),
  addProps(({ user }) => ({
    isStealthMode: user?.isStealth,
  })),
  addState('allMedias', 'setAllMedias', [] as any[]),
  addEffect(
    ({ topMedias, trendingMedias, setAllMedias }) =>
      () => {
        let finalTrending = [] as any[]
        if (trendingMedias) {
          finalTrending = trendingMedias.data.media.filter(
            (media) => media.type === 'media'
          )
        }

        if (topMedias && finalTrending) {
          const all = [...topMedias, ...finalTrending]
          setAllMedias(all)
        } else if (topMedias && !finalTrending) {
          setAllMedias(topMedias)
        } else if (finalTrending && !topMedias) {
          setAllMedias(finalTrending)
        }
      },
    ['topMedias', 'trendingMedias']
  ),
  addTranslationHelpers,
  ({
    product: { id, image, brandName, name, price },
    schemaOrg,
    mediaId,
    userListId,
    user,
    radars,
    allMedias,
    onShop,
    classes,
    imgSrc,
    setImgSrc,
    t,
  }) => (
    <>
      <Helmet>
        <meta property="og:type" content="product" />
        <meta property="og:image" content={image.full} />
        {price && (
          <meta property="product:price:amount" content={formattedUsd(price)} />
        )}
        {price && <meta property="product:price:currency" content="USD" />}
        <script type="application/ld+json">{schemaOrg}</script>
        <link
          rel="canonical"
          href={`${process.env.REACT_APP_SHOPPER_URL}/products/${id}`.replace(
            /(?<!:)\/+/g,
            '/'
          )}
        />
      </Helmet>
      <div className={classes.container}>
        {hasHistory() ? (
          <button
            style={{
              position: 'absolute',
              top: 16,
              left: 16,
              width: 44,
              height: 44,
              zIndex: 1000,
              backgroundColor: '#1010104D',
              borderRadius: '8px',
              border: 'none',
              backdropFilter: 'blur(15px)',
              cursor: 'pointer',
            }}
            onClick={navBackOrHomepage}
          >
            <BackIcon />
          </button>
        ) : (
          <button
            style={{
              backgroundImage: `url(${cornerLogo})`,
              zIndex: 100,
              position: 'absolute',
              top: 0,
              left: 0,
              width: 100,
              height: 90,
              border: 'none',
              cursor: 'pointer',
              backgroundPosition: '0 -70px',
              backgroundRepeat: 'no-repeat',
              backgroundColor: 'transparent',
            }}
            onClick={navBackOrHomepage}
          />
        )}
        <button onClick={onShop} className={classes.imageContainer}>
          <img src={image.lowres} alt="" className={classes.blurredImage} />
          <img
            src={imgSrc}
            onLoad={() => setImgSrc(image.full)}
            alt={t('productDetail.imageAlt', { brand: brandName, name })}
            className={classes.image}
          />
        </button>
        <div className={classes.infoContainer}>
          <div className={classes.brand}>
            <div>{brandName}</div>
            <Button onClick={onShop}>
              <img
                src={productExplore}
                alt={
                  price != null ? 'productDetail.shop' : 'productDetail.explore'
                }
              />
            </Button>
          </div>
          <div className={classes.namePriceContainer}>
            <p className={classes.name}>{name}</p>
            {price && <p className={classes.price}>{formattedUsd(price)}</p>}
          </div>
          <div className={classes.buttonContainer}>
            <BookmarkButton
              mediaId={mediaId}
              userListId={userListId}
              creatorId={user?.id}
              productId={id}
              roundupStyle={'mini'}
            />
            <WhiteButton
              title={t(
                price != null ? 'productDetail.shop' : 'productDetail.explore'
              )}
              onClick={onShop}
              className={classes.shopButton}
            />
          </div>
        </div>
        {allMedias.length > 0 && (
          <>
            <div className={classes.mediasContainer}>
              {allMedias.slice(0, 4).map((media) => (
                <React.Fragment key={media.id}>
                  <Link to={makeMediaUrl({ mediaId: media.id })}>
                    <img
                      src={media.thumbnail}
                      alt={
                        media.content?.value?.altText
                          ? media.content.value.altText
                          : ''
                      }
                      className={classes.mediaImage}
                    />
                  </Link>
                </React.Fragment>
              ))}
            </div>
          </>
        )}
        <a href={APP_WAITLIST_URL} className={classes.downloadAppBannerHolder}>
          <img
            src={downloadAppBanner}
            className={classes.downloadAppBanner}
            alt="Download ShopRodeo App"
          />
        </a>
        {allMedias.length > 4 && (
          <>
            <div className={classes.mediasContainer}>
              {allMedias.slice(4, 8).map((media) => (
                <React.Fragment key={media.id}>
                  <Link to={makeMediaUrl({ mediaId: media.id })}>
                    <img
                      src={media.thumbnail}
                      alt={
                        media.content?.value?.altText
                          ? media.content.value.altText
                          : ''
                      }
                      className={classes.mediaImage}
                    />
                  </Link>
                </React.Fragment>
              ))}
            </div>
          </>
        )}
        {radars.state === 'loaded' && radars.data?.data.length > 0 && (
          <>
            <div className={classes.radarsContainer}>
              <button
                onClick={() => {
                  if (radars.data?.data[0].url) {
                    window.open(radars.data?.data[0].url, '_blank')
                  }
                }}
                className={classes.radarLink}
                style={{
                  backgroundImage: `url(${radars.data?.data[0].image ?? ''})`,
                }}
              >
                <div className={classes.radarTitle}>
                  {radars.data?.data[0].title}
                </div>
                <div className={classes.radarIntro}>
                  {radars.data?.data[0].intro}
                </div>
              </button>
            </div>
          </>
        )}
        <div className={classes.affiliateDisclosure}>
          {t('affiliateDisclosure')}
        </div>
      </div>
    </>
  )
)
